import { useLocale, useT } from '../i18n';
import { usePrice } from '../lib/currency';
import { fmtDateDow } from '../lib/format';
import { gaEvent } from '../lib/saved';
import { showToast } from './Toast';
import type { Event } from '../types';
import { eventPath } from '../../shared/slug.mjs';
import { isLa28 } from '../../shared/legal.mjs';

/** The arrow-out-of-a-box share mark, in the icon set's 1.6 px stroke (src/components/icons.tsx). */
export const ShareIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 14.5V3.5M8 7.5l4-4 4 4" />
    <path d="M7.5 10.5H6a1.5 1.5 0 0 0-1.5 1.5v7A1.5 1.5 0 0 0 6 20.5h12a1.5 1.5 0 0 0 1.5-1.5v-7a1.5 1.5 0 0 0-1.5-1.5h-1.5" />
  </svg>
);

/** "KATSEYE, Wed, Oct 28": which event a control acts on, for screen readers (Save and Share). */
export const eventLabel = (event: Pick<Event, 'artist' | 'name' | 'date'>, locale: string) => `${event.artist ?? event.name}, ${fmtDateDow(event.date, locale)}`;

/** Shared links always name the real site, whatever host the page was opened on. */
const SITE = 'https://tixinfo.com';

const copyText = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    /* no async clipboard (insecure context, permissions): the old way */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
};

/**
 * Share an event: the phone's own share sheet where there is one (group chats are where concert
 * plans get made), else the link is copied and a toast says so.
 *
 * The message names the act, venue and date, and quotes the price the page shows: `from` is the
 * lowest fees-included price on the board as the visitor sees it, after the live HelloTickets
 * refresh (EventDetail's displayEvent; src/components/SaveButton.tsx HeroActions), formatted in
 * the visitor's currency. Never the built price behind it, which the refresh may have moved (it
 * once shared "From $47 all-in" while the page said $95): a stale, lower price sent to friends is
 * a price claim nobody can get. No fees-included price on the board: no price in the message. An
 * LA28 session says what its prices are: unofficial resale (shared/legal.mjs). The link is the
 * canonical page with utm_source=share, so shared visits are countable (and the recipient is
 * offered "Save this event": SaveButton.tsx). Its accessible name starts with the word it shows.
 */
const ShareButton = ({ event, from }: { event: Event; from: number | null }) => {
  const t = useT();
  const locale = useLocale();
  const price = usePrice();

  // No await before navigator.share: Safari only opens the sheet inside the tap itself.
  const share = async () => {
    const act = event.artist ?? event.name;
    const title = `${act} · ${event.venue} · ${fmtDateDow(event.date, locale)}`;
    const floor = typeof from === 'number' && from > 0 ? from : null;
    const text = isLa28(event)
      ? floor
        ? t('Unofficial resale prices from {price} all-in, compared on TixInfo', { price: price(floor) })
        : t('Unofficial resale prices, compared on TixInfo')
      : floor
        ? t('From {price} all-in, compared on TixInfo', { price: price(floor) })
        : t('Compared on TixInfo');
    const url = `${SITE}${eventPath(event)}?utm_source=share&utm_medium=social`;
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, text, url });
        gaEvent('share', { method: 'native', content_type: 'event' });
        return;
      } catch (err) {
        // The visitor closed the sheet: nothing to do. Anything else: copy the link instead.
        if ((err as { name?: string })?.name === 'AbortError') return;
      }
    }
    if (await copyText(url)) {
      gaEvent('share', { method: 'copy', content_type: 'event' });
      showToast({ message: t('Link copied') });
    } else {
      showToast({ message: t('Couldn’t copy the link. Copy it from the address bar instead.') });
    }
  };

  return (
    <button
      type="button"
      data-share=""
      onClick={() => void share()}
      className="group/act grid h-11 min-w-11 place-items-center rounded-full outline-offset-0"
    >
      <span className="inline-flex h-9 min-w-9 items-center justify-center gap-1.5 rounded-full border border-border bg-background/85 px-2.5 text-[13px] font-medium text-foreground shadow-sm backdrop-blur transition-colors group-hover/act:border-foreground/30 sm:px-3.5">
        <ShareIcon className="size-[17px]" />
        <span className="sr-only sm:not-sr-only">{t('Share')}</span>
        <span className="sr-only"> {eventLabel(event, locale)}</span>
      </span>
    </button>
  );
};

export default ShareButton;
